import { memo, useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import _isEmpty from 'lodash/isEmpty';
//Local files
import { CardItem } from './VoiceFlows.styled';
import VoiceAnalysesList from 'components/Analyses/VoiceAnalyses/List/List';
import PhoneField from 'components/Common/PhoneField/PhoneField';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useApp from 'hooks/useApp';
import useCustomSelector from 'hooks/useCustomSelector';
import useSuccess from 'hooks/useSuccess';
import useVoiceFlows from 'hooks/useVoiceFlows';
import useVoiceAnalyses from 'hooks/useVoiceAnalyses';
import { DELETE_VOICE_FLOW } from 'helpers/confirmations';
import { checkEmptyString, checkValidEmail, needToUpdateField } from 'helpers';
import { voiceFlowsSelectors } from "features/voiceFlows";

const VoiceFlows = () => {
  const { openDeleteDialog } = useApp();
  const { setSuccess } = useSuccess();
  const { getVoiceFlow, createVoiceFlow, updateVoiceFlow } = useVoiceFlows();
  const { getVoiceAnalyses, clearLocalVoiceAnalyses } = useVoiceAnalyses();
  const id = useCustomSelector(voiceFlowsSelectors.getVoiceFlowId);
  const voiceFlow = useCustomSelector(voiceFlowsSelectors.getVoiceFlowData);
  const fetching = useCustomSelector(voiceFlowsSelectors.voiceFlowIsFetching);
  const [email, setEmail] = useState({ value: '', error: '' });
  const [phone, setPhone] = useState({ value: '', formattedValue: '', error: '' });
  const [prompt, setPrompt] = useState({ value: 'Summarize the content of this text into email subject line', error: '' });
  const [edit, setEdit] = useState(false);
  const [firstTimeOpened, setFirstTimeOpened] = useState(true);

  const handleEntering = (voice_flow) => {
    setEmail({ value: voice_flow?.email, error: '' });
    setPhone({ value: voice_flow?.phone, formattedValue: voice_flow?.phone, error: '' });
    setPrompt({ value: voice_flow?.prompt, error: '' });
  };
  const handleExited = () => {
    setEmail({ value: '', error: '' });
    setPhone({ value: '', formattedValue: '', error: '' });
    setPrompt({ value: 'Summarize the content of this text into email subject line', error: '' });
    setEdit(false);
  };
  const handleSave = () => {
    let hasError = false;

    if (!checkValidEmail(email.value)) {
      setEmail(prev => ({ ...prev, error: `Email is invalid` }) );
      hasError = true;
    }
    if (!checkEmptyString(phone.value)) {
      setPhone(prev => ({ ...prev, error: `Phone can't be empty` }) );
      hasError = true;
    }
    if (!checkEmptyString(prompt.value)) {
      setPrompt(prev => ({ ...prev, error: `Prompt can't be empty` }) );
      hasError = true;
    }
    if (phone.error) hasError = true;
    if (hasError) return;

    const voice_flow = {
      ...(needToUpdateField(voiceFlow?.email, email.value) && {  email: email.value }),
      ...(needToUpdateField(voiceFlow?.phone, phone.value) && {  phone: phone.value }),
      ...(needToUpdateField(voiceFlow?.prompt, prompt.value) && { prompt: prompt.value }),
    };

    if (!_isEmpty(voice_flow)) {
      if (!!id) {
        updateVoiceFlow(voice_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully updated');
          }
        })
        .finally(() => setEdit(false))
      } else {
        createVoiceFlow(voice_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully created');
          }
        })
      }
    } 
  }
  const handleDelete = () => openDeleteDialog({ id: '', type: DELETE_VOICE_FLOW });
  const fetchVoiceAnalyses = useCallback(() => {
    getVoiceAnalyses({ offset: 0 })
  }, [getVoiceAnalyses]);
  
  useEffect(() => {
    if (!id) {
      handleExited();
      clearLocalVoiceAnalyses();
    };
  }, [id, clearLocalVoiceAnalyses]);
  useEffect(() => {
    if (firstTimeOpened) {
      getVoiceFlow()
      .then(({ payload, error }) => {
        if (!error) {
          const { voice_flow } = payload;
          handleEntering(voice_flow);
          fetchVoiceAnalyses();
        }
      })
      .finally(() => setFirstTimeOpened(false));
    }
  }, [getVoiceFlow, fetchVoiceAnalyses, firstTimeOpened]);

  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto', p: 5 }}>
      <Container maxWidth={'sm'}>
        <Card sx={{ p: 2 }}>
          <CardItem>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Configuration</Typography>
            <Box>
              {!id && <Button variant='outlined' disabled={fetching} onClick={handleSave}>Create</Button>}
              {id && !edit && <Button variant='outlined' disabled={fetching} onClick={() => setEdit(true)}>Edit</Button>}
              {id && edit && <Button variant='outlined' disabled={fetching} sx={{ mr: '5px' }} onClick={handleSave}>Update</Button>}
              {id && edit && <Button variant='outlined' sx={{ color: '#E30000', borderColor: '#E30000' }} disabled={fetching} onClick={handleDelete}>Delete</Button>}
            </Box>
          </CardItem>
          <CardItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <PhoneField
              id='phone'
              fullWidth
              label='Incoming voicemail number'
              disabled={fetching || (!!id && !edit)}
              formattedValue={phone.formattedValue}
              error={!!phone.error}
              helperText={phone.error}
              onChange={({ value, formattedValue, error }) => setPhone({ value, formattedValue, error })}
            />
            <TextField
              fullWidth
              label='Send to'
              disabled={fetching || (!!id && !edit)}
              value={email.value}
              onChange={({ target: { value } }) => setEmail({ value, error: '' })}
              helperText={email.error}
              error={!!email.error}
            />
            <TextField
              fullWidth
              label='Prompt'
              multiline
              minRows={3}
              maxRows={10}
              disabled={fetching || (!!id && !edit)}
              helperText={prompt.error}
              error={!!prompt.error}
              value={prompt.value}
              onChange={({ target: { value } }) => setPrompt({ value, error: '' })}
            />
          </CardItem>
        </Card>
        <Card sx={{ p: 2, mt: 3 }}>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>History</Typography>
          <VoiceAnalysesList />
        </Card>    
      </Container>
    </Box>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <VoiceFlows {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);